import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useContent } from '@/context/ContentContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Phone, Mail, Search, Trash2, CreditCard, Check } from 'lucide-react';
import SendPaymentInline from '@/components/admin/SendPaymentInline';

type Submission = {
  id: string;
  name: string;
  phone: string;
  email: string;
  answers: Record<string, string | string[]>;
  summary: string | null;
  status: 'new' | 'in_review' | 'contacted' | 'done';
  notes: string | null;
  created_at: string;
  updated_at: string | null;
};

const STATUS_LABELS: Record<Submission['status'], string> = {
  new: 'חדש',
  in_review: 'בבדיקה',
  contacted: 'יצרנו קשר',
  done: 'טופל',
};
const STATUS_COLORS: Record<Submission['status'], string> = {
  new: 'bg-red-100 text-red-700 border-red-200',
  in_review: 'bg-amber-100 text-amber-700 border-amber-200',
  contacted: 'bg-blue-100 text-blue-700 border-blue-200',
  done: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export default function SubmissionsInbox() {
  const { personalRxQuestions } = useContent();
  const [rows, setRows] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Submission['status']>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [paymentOpenId, setPaymentOpenId] = useState<string | null>(null);

  const labelByKey = useMemo(() => {
    const m = new Map<string, string>();
    (personalRxQuestions ?? []).forEach((q) => m.set(q.id, q.label));
    return m;
  }, [personalRxQuestions]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('personal_rx_submissions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);
    setLoading(false);
    if (error) {
      toast.error('שגיאה בטעינת פניות', { description: error.message });
      return;
    }
    setRows((data as unknown as Submission[]) ?? []);
  };

  useEffect(() => {
    load();
    // Poll every 30s instead of realtime (table is no longer in the realtime
    // publication to avoid leaking sensitive submissions over channels).
    const interval = setInterval(() => load(), 30_000);
    return () => clearInterval(interval);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        r.name?.toLowerCase().includes(q) ||
        r.phone?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q)
      );
    });
  }, [rows, statusFilter, search]);

  const updateRow = async (id: string, patch: Partial<Submission>) => {
    const { error } = await supabase.from('personal_rx_submissions').update(patch).eq('id', id);
    if (error) {
      toast.error('שגיאה בשמירה', { description: error.message });
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const deleteRow = async (id: string) => {
    if (!confirm('למחוק את הפנייה לצמיתות?')) return;
    const { error } = await supabase.from('personal_rx_submissions').delete().eq('id', id);
    if (error) {
      toast.error('שגיאה במחיקה', { description: error.message });
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
    toast.success('נמחק');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש לפי שם, טלפון, אימייל…"
            className="pr-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">כל הסטטוסים</SelectItem>
            <SelectItem value="new">חדש</SelectItem>
            <SelectItem value="in_review">בבדיקה</SelectItem>
            <SelectItem value="contacted">יצרנו קשר</SelectItem>
            <SelectItem value="done">טופל</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filtered.length} פניות
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">טוען…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">אין פניות להצגה</Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const expanded = expandedId === r.id;
            const answerCount = Object.keys(r.answers ?? {}).length;
            return (
              <Card key={r.id} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : r.id)}
                  className="w-full text-right p-4 flex items-center gap-4 hover:bg-neutral-50"
                >
                  <Badge className={`shrink-0 ${STATUS_COLORS[r.status]}`} variant="outline">
                    {STATUS_LABELS[r.status]}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.name}</div>
                    <div className="text-xs text-muted-foreground truncate flex gap-3">
                      <span>{r.phone}</span>
                      <span>{r.email}</span>
                      <span>· {answerCount} תשובות</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 tabular-nums">
                    {new Date(r.created_at).toLocaleDateString('he-IL')}{' '}
                    {new Date(r.created_at).toLocaleTimeString('he-IL', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                  {expanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {expanded && (
                  <div className="border-t bg-neutral-50/50 p-5 space-y-5">
                    <div className="flex flex-wrap gap-2 items-center justify-between pb-3 border-b">
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant={paymentOpenId === r.id ? 'secondary' : 'default'}
                          disabled={!r.email}
                          onClick={() =>
                            setPaymentOpenId(paymentOpenId === r.id ? null : r.id)
                          }
                        >
                          <CreditCard className="h-4 w-4 me-1" /> שליחת קישור תשלום
                        </Button>
                        {r.status !== 'done' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateRow(r.id, { status: 'done' })}
                          >
                            <Check className="h-4 w-4 me-1" /> סמן כטופל
                          </Button>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => deleteRow(r.id)}
                      >
                        <Trash2 className="h-4 w-4 me-1" /> מחיקה
                      </Button>
                    </div>

                    {paymentOpenId === r.id && (
                      <SendPaymentInline
                        email={r.email}
                        name={r.name}
                        phone={r.phone}
                        note={`פנייה #${r.id.slice(0, 8)} מ-${new Date(r.created_at).toLocaleDateString('he-IL')}`}
                        tags={['personal-rx', 'admin-invoice']}
                        onClose={() => setPaymentOpenId(null)}
                        onSent={() => updateRow(r.id, { status: 'contacted' })}
                      />
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">סטטוס</Label>
                        <Select
                          value={r.status}
                          onValueChange={(v) =>
                            updateRow(r.id, { status: v as Submission['status'] })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(Object.keys(STATUS_LABELS) as Submission['status'][]).map((s) => (
                              <SelectItem key={s} value={s}>
                                {STATUS_LABELS[s]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">צור קשר</Label>
                        <div className="flex gap-2">
                          <a
                            href={`tel:${r.phone}`}
                            className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-md hover:bg-white"
                          >
                            <Phone className="h-3.5 w-3.5" /> טלפון
                          </a>
                          <a
                            href={`mailto:${r.email}`}
                            className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-md hover:bg-white"
                          >
                            <Mail className="h-3.5 w-3.5" /> אימייל
                          </a>
                          <a
                            href={`https://wa.me/${r.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-md hover:bg-white"
                          >
                            וואטסאפ
                          </a>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">תשובות הלקוחה</Label>
                      <Card className="p-3 bg-white text-sm">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                          {Object.entries(r.answers ?? {}).map(([k, v]) => (
                            <div key={k} className="flex flex-col">
                              <dt className="text-xs text-muted-foreground">
                                {labelByKey.get(k) ?? k}
                              </dt>
                              <dd className="font-medium">
                                {Array.isArray(v) ? v.join(', ') : v}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </Card>
                    </div>

                    <div>
                      <Label className="text-xs">הערות פנימיות</Label>
                      <Textarea
                        defaultValue={r.notes ?? ''}
                        rows={3}
                        placeholder="הערות שיראו רק בצוות…"
                        onBlur={(e) => {
                          if ((r.notes ?? '') !== e.target.value) {
                            updateRow(r.id, { notes: e.target.value });
                          }
                        }}
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">
                        ההערות נשמרות אוטומטית כשעוזבים את השדה.
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
